import { Component, Input, OnChanges, SimpleChanges, output, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { TRANSLOCO_SCOPE, TranslocoModule } from '@jsverse/transloco';

import { QaiIconComponent, QaiInputComponent } from '@qai/ui';
import { ProcessMarkdownPipe } from 'src/app/pipes/process-markdown.pipe';
import { CallRefinementStatus } from 'src/app/utils/enums/self-serve/call-refinement-status.enum';
import { CALL_DETAILS_SECTION_LABELS } from 'src/app/utils/generated-call-details.util';
import { isComposingKeyEvent } from 'src/app/utils/keyboard-event.util';

const LOCKED_STATUSES = [
  CallRefinementStatus.Thinking,
  CallRefinementStatus.Warning,
];

@Component({
  selector: 'generated-call-details',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, QaiIconComponent, QaiInputComponent, ProcessMarkdownPipe, TranslocoModule],
  templateUrl: './generated-call-details.component.html',
  styleUrls: ['./generated-call-details.component.scss'],
  providers: [{ provide: TRANSLOCO_SCOPE, useValue: 'simulationBuilder' }],
})
export class GeneratedCallDetailsComponent implements OnChanges {
  @Input() contentMarkdown: string = '';
  /** Caps the text-content area's height so it scrolls internally instead of
   * pushing the prompt row / modal footer off-screen. Empty string = no cap. */
  @Input() contentMaxHeight: string = '';
  /** Driven by the consumer's refinement-workflow polling. Idle = normal
   * input; Thinking/Warning lock the input; Updated is a success label that
   * stays up until the consumer clears it (e.g. on leaving the view).
   * Warning renders a dismissible banner (content can't be auto-refined
   * further) and keeps the input locked; Error renders a plain inline
   * message (the refinement attempt itself failed) and leaves the input
   * enabled so the user can retry. */
  @Input() refinementStatus: CallRefinementStatus = CallRefinementStatus.Idle;
  /** True while the consumer is re-fetching content after a refinement
   * completes — swaps the content area for a per-section skeleton instead
   * of the real markdown. The consumer keeps refinementStatus at Thinking
   * for this whole window, so the input stays locked until real content
   * (not just the skeleton) is ready. */
  @Input() isContentRefreshing = false;
  /** When true, a QC Admin has manually overridden the converse prompt for
   * this simulation. The refinement input is permanently disabled and an
   * informational banner is shown. Driven exclusively by the API response —
   * never set client-side. */
  @Input() isRefinementBlocked = false;

  readonly CallRefinementStatus = CallRefinementStatus;
  /** Local UI state — tracks whether the author dismissed the override
   * banner. Does not affect the underlying flag. */
  readonly overrideBannerDismissed = signal(false);
  readonly skeletonSectionLabels = CALL_DETAILS_SECTION_LABELS;
  /** Placeholder lines rendered under each skeleton heading. */
  readonly skeletonLines = [0, 1, 2];

  readonly requestChangesSubmitted = output<string>();
  readonly errorDismissed = output<void>();

  requestChangesControl = new FormControl('');

  get isInputLocked(): boolean {
    return LOCKED_STATUSES.includes(this.refinementStatus);
  }

  /** Template-facing lock state — also true right after submit, before
   * refinementStatus has had a chance to confirm the trigger call
   * round-tripped (see handleSubmitRequestChanges). Kept separate from
   * isInputLocked, which ngOnChanges uses to decide whether to re-enable the
   * control based solely on the authoritative refinementStatus: folding
   * requestChangesControl.disabled in there would deadlock, since once
   * disabled it would never look "unlocked" again for ngOnChanges to act on. */
  get isSubmitLocked(): boolean {
    return this.requestChangesControl.disabled || this.isInputLocked;
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Admin override is a permanent lock — takes precedence over polling status.
    if (this.isRefinementBlocked) {
      this.requestChangesControl.disable();
      return;
    }

    const statusChange = changes['refinementStatus'];
    if (!statusChange) return;

    if (this.isInputLocked) {
      this.requestChangesControl.disable();
      return;
    }

    this.requestChangesControl.enable();
    if (LOCKED_STATUSES.includes(statusChange.previousValue)) {
      this.requestChangesControl.reset();
    }
  }

  handleRequestChangesKeydown(event: Event): void {
    if (isComposingKeyEvent(event as KeyboardEvent)) return;
    this.handleSubmitRequestChanges();
  }

  handleSubmitRequestChanges(): void {
    const value = this.requestChangesControl.value?.trim();
    if (!value) return;

    // Lock optimistically — refinementStatus only flips to Thinking once the
    // consumer's trigger-refinement call round-trips and polling starts, and
    // the gap between submit and that update would otherwise let the user
    // submit again mid-flight. ngOnChanges takes over once the real status
    // arrives (staying disabled for Thinking, re-enabling otherwise).
    this.requestChangesControl.disable();
    this.requestChangesSubmitted.emit(value);
  }

  handleDismissWarning(): void {
    this.errorDismissed.emit();
  }

  dismissOverrideBanner(): void {
    this.overrideBannerDismissed.set(true);
  }
}
